if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 990px)');

        this.initDotsPagination();
        this.initDragToScroll();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initDotsPagination() {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.slider) return;
        const dots = Array.from(viewer.querySelectorAll('.product__media-dot'));
        if (!dots.length) return;

        const visibleSlides = () =>
          Array.from(viewer.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.clientWidth > 0);

        const setActiveDot = (index) => {
          dots.forEach((dot, dotIndex) => {
            dot.classList.toggle('slider-counter__link--active', dotIndex === index);
          });
        };

        dots.forEach((dot, index) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            const target = visibleSlides()[index];
            if (!target) return;
            viewer.slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
          });
        });

        viewer.addEventListener('slideChanged', (event) => {
          const index = visibleSlides().indexOf(event.detail.currentElement);
          if (index > -1) setActiveDot(index);
        });
      }

      initDragToScroll() {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.slider || !viewer.hasAttribute('data-enable-drag')) return;
        const slider = viewer.slider;
        let isDown = false;
        let dragged = false;
        let startX = 0;
        let startScrollLeft = 0;

        const stopDrag = () => {
          if (!isDown) return;
          isDown = false;
          slider.classList.remove('is-dragging');
        };

        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') return;
          isDown = true;
          dragged = false;
          startX = event.clientX;
          startScrollLeft = slider.scrollLeft;
          slider.classList.add('is-dragging');
        });

        window.addEventListener('pointermove', (event) => {
          if (!isDown) return;
          const distance = event.clientX - startX;
          if (Math.abs(distance) > 5) dragged = true;
          slider.scrollLeft = startScrollLeft - distance;
        });

        window.addEventListener('pointerup', stopDrag);
        window.addEventListener('pointercancel', stopDrag);

        slider.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;
            event.preventDefault();
            event.stopPropagation();
            dragged = false;
          },
          true
        );
      }
    }
  );
}
